import { publicAssetUrl } from "@/src/lib/publicAssetUrl"

export type SoundDefinition = {
    src: string | readonly string[]
    volume?: number
    loop?: boolean
    html5?: boolean
    preload?: boolean
}

export const MUSIC_DEFAULT_VOLUME = 0.3


// Central sound registry. Add entries here as assets land in `public/sounds/`.
export const SOUNDS = {
    // character sounds
    characterJump1: { src: [publicAssetUrl('sounds/character/character-jump-1.aac')], volume: 1.5 },
    characterJump2: { src: [publicAssetUrl('sounds/character/character-jump-2.aac')], volume: 1.5 },
    characterJump3: { src: [publicAssetUrl('sounds/character/character-jump-3.aac')], volume: 1.5 },
    characterCrash1: { src: [publicAssetUrl('sounds/character/character-crash-1.aac')], volume: 1.5 },
    characterCrash2: { src: [publicAssetUrl('sounds/character/character-crash-2.aac')], volume: 1.5 },
    characterCrash3: { src: [publicAssetUrl('sounds/character/character-crash-3.aac')], volume: 1.5 },
    characterCrashFinal: { src: [publicAssetUrl('sounds/character/character-crash-final.aac')], volume: 1.5 },
    characterLaneSwitch1: { src: [publicAssetUrl('sounds/character/character-lane-switch-1.aac')], volume: 1.5 },
    characterLaneSwitch2: { src: [publicAssetUrl('sounds/character/character-lane-switch-2.aac')], volume: 1.5 },
    characterLaneSwitch3: { src: [publicAssetUrl('sounds/character/character-lane-switch-3.aac')], volume: 1.5 },
    characterAirSwitch1: { src: [publicAssetUrl('sounds/character/character-air-switch-1.aac')], volume: 1.5 },
    characterAirSwitch2: { src: [publicAssetUrl('sounds/character/character-air-switch-2.aac')], volume: 1.5 },
    characterAirSwitch3: { src: [publicAssetUrl('sounds/character/character-air-switch-3.aac')], volume: 1.5 },
    characterUpgrade: { src: [publicAssetUrl('sounds/character/character-upgrade.aac')], volume: 1.5 },
    characterUpgradeFinal: { src: [publicAssetUrl('sounds/character/character-upgrade-final.aac')], volume: 1.5 },
    characterTunnelEnter: { src: [publicAssetUrl('sounds/character/character-tunnel-enter.aac')], volume: 1.5 },
    characterPlane: { src: [publicAssetUrl('sounds/character/character-plane.aac')], volume: 1.5 },
    characterLightning: { src: [publicAssetUrl('sounds/character/character-lightning.aac')], volume: 1.5 },
    characterHeartCollect: { src: [publicAssetUrl('sounds/character/character-heart-collect.aac')], volume: 1.75 },
    characterFinishLine: { src: [publicAssetUrl('sounds/character/character-finish-line.aac')], volume: 1.5 },
    characterStretch: { src: [publicAssetUrl('sounds/character/character-stretch.aac')], volume: 1.5 },
    characterConfig1: { src: [publicAssetUrl('sounds/character/character-config-1.aac')], volume: 1.5 },
    characterConfig2: { src: [publicAssetUrl('sounds/character/character-config-2.aac')], volume: 1.5 },
    characterConfig3: { src: [publicAssetUrl('sounds/character/character-config-3.aac')], volume: 1.5 },
    characterHighFive: { src: [publicAssetUrl('sounds/character/character-high-five.aac')], volume: 1.5 },
    // sfx sounds
    powerUpCollect: { src: [publicAssetUrl('sounds/sfx/power-up-collect.aac')], volume: 1 },
    coinCollect: { src: [publicAssetUrl('sounds/sfx/coin-collect.aac')], volume: 1 },
    heartCollect: { src: [publicAssetUrl('sounds/sfx/heart-collect.aac')], volume: 1 },
    shieldCrash: { src: [publicAssetUrl('sounds/sfx/shield-deactivated.aac')], volume: 1 },
    heartLost: { src: [publicAssetUrl('sounds/sfx/heart-lost.aac')], volume: 1 },
    shieldCollect: { src: [publicAssetUrl('sounds/sfx/shield-activated.aac')], volume: 1 },
    batteryReady: { src: [publicAssetUrl('sounds/sfx/battery-ready.aac')], volume: 1 },
    batteryActivate: { src: [publicAssetUrl('sounds/sfx/battery-activated.aac')], volume: 1 },
    batteryDeactivate: { src: [publicAssetUrl('sounds/sfx/battery-deactivated.aac')], volume: 1 },
    tunnelEnter: { src: [publicAssetUrl('sounds/sfx/tunnel-enter.aac')], volume: 1 },
    tunnelExit: { src: [publicAssetUrl('sounds/sfx/tunnel-exit.aac')], volume: 1 },
    tunnelSpeed: { src: [publicAssetUrl('sounds/sfx/tunnel-speed.aac')], volume: 1, loop: true },
    levelName: { src: [publicAssetUrl('sounds/sfx/level-name.aac')], volume: 1 },
    countdown: { src: [publicAssetUrl('sounds/music/countdown.aac')], volume: 1 },
    tradingCardReveal: { src: [publicAssetUrl('sounds/sfx/tradingcard-reveal.m4a')], volume: 1 },
    upgrade: { src: [publicAssetUrl('sounds/sfx/upgrade.aac')], volume: 1 },
    // ui sounds
    buttonPrimary: { src: [publicAssetUrl('sounds/ui/button-primary.aac')], volume: 1 },
    buttonSecondary: { src: [publicAssetUrl('sounds/ui/button-secondary.aac')], volume: 1 },
    buttonDice: { src: [publicAssetUrl('sounds/ui/button-dice.aac')], volume: 1 },
    shortWoosh: { src: [publicAssetUrl('sounds/ui/short-woosh.aac')], volume: 1 },
    longWoosh: { src: [publicAssetUrl('sounds/ui/long-woosh.aac')], volume: 1 },
    changeColor: { src: [publicAssetUrl('sounds/ui/change-color.aac')], volume: 1 },
    popupOpen: { src: [publicAssetUrl('sounds/ui/popup-open.aac')], volume: 1 },
    popupClose: { src: [publicAssetUrl('sounds/ui/popup-close.aac')], volume: 1 },
    // music
    musicTheme: { src: [publicAssetUrl('sounds/music/life-rush-theme.m4a')], volume: MUSIC_DEFAULT_VOLUME, loop: true },
    musicPause: { src: [publicAssetUrl('sounds/music/pause-theme.m4a')], volume: MUSIC_DEFAULT_VOLUME },
    musicLevel1: { src: [publicAssetUrl('sounds/music/level-1.m4a')], volume: MUSIC_DEFAULT_VOLUME },
    musicLevel2: { src: [publicAssetUrl('sounds/music/level-2.m4a')], volume: MUSIC_DEFAULT_VOLUME },
    musicLevel3: { src: [publicAssetUrl('sounds/music/level-3.m4a')], volume: MUSIC_DEFAULT_VOLUME },
    musicStarCollect: { src: [publicAssetUrl('sounds/music/powerup-star.aac')], volume: MUSIC_DEFAULT_VOLUME, loop: false },
    musicVictoryTheme: { src: [publicAssetUrl('sounds/music/victory-theme.aac')], volume: MUSIC_DEFAULT_VOLUME, loop: true },
    musicGameOverTheme: { src: [publicAssetUrl('sounds/music/game-over-theme.aac')], volume: MUSIC_DEFAULT_VOLUME, loop: true },
    musicTunnelTheme: { src: [publicAssetUrl('sounds/music/tunnel-theme.aac')], volume: MUSIC_DEFAULT_VOLUME, loop: true },
} as const satisfies Record<string, SoundDefinition>

function soundDefinitionUrls(definition: SoundDefinition): string[] {
    return typeof definition.src === 'string' ? [definition.src] : [...definition.src]
}

export const soundAssetUrls = [
    ...new Set(Object.values(SOUNDS).flatMap(soundDefinitionUrls)),
] as const

// Becomes a union of registered sound keys once entries are added to `SOUNDS`
export type SoundId = keyof typeof SOUNDS extends never ? string : keyof typeof SOUNDS
